import { get, set } from 'lodash-es';

/**
 * Orders an array of objects by a specified key path
 * @param items Array of objects to order
 * @param keyPath Path to the property used for ordering (e.g., 'metadata.order')
 * @returns Ordered array with sequential values assigned to the key path
 */
export function sortItems<T extends object>(items: T[], keyPath: string): T[] {
  // Clone the array to avoid mutating the original
  const result = [...items];

  // Sort the array
  result.sort((a, b) => {
    const valueA = get(a, keyPath);
    const valueB = get(b, keyPath);

    // Both have values - maintain relative order
    if (isValidNumber(valueA) && isValidNumber(valueB)) {
      return valueA - valueB;
    }

    // Only A has value - A comes first
    if (isValidNumber(valueA)) return -1;

    // Only B has value - B comes first
    if (isValidNumber(valueB)) return 1;

    // Neither has value - maintain original order
    return 0;
  });

  // Assign sequential numbers
  result.forEach((item, index) => {
    set(item, keyPath, index + 1);
  });

  return result;
}

/**
 * Helper to check if a value is a valid number
 */
function isValidNumber(value: any): boolean {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}
